import React, { type FunctionComponent, useCallback } from 'react'
import styled from '@emotion/styled'
import jsonexport from 'jsonexport/dist'
import { type Moment } from 'moment'
import { DownloadIcon } from '@instacart/ids-core'
import { Button, spacing } from '@retailer-platform/shared-components'
import { useRetailerId } from 'utils/routing/params.hooks'
import { useDomainMessages } from 'domains/aggregate-store-performance/utils/domain/intl'
import {
  momentToQueryStringDate,
  getEndOfWeek,
  toStartOfWeek,
} from 'domains/aggregate-store-performance/utils/helpers/AggregateStorePerformanceOverviewPage.helpers'
import { useWarehouseContext } from 'utils/contexts/warehouse/WarehouseContext.hooks'
import { trackExportClicked } from 'domains/aggregate-store-performance/utils/helpers/AggregateStorePerformanceOverviewPage.tracking'
import {
  downloadCsvFile,
  hasDedicatedRates,
} from 'domains/aggregate-store-performance/utils/helpers/AggregateStorePerformanceOverviewPage.helpers'
import {
  HISTORICAL_DEDICATED_STORE_METRIC_COLUMN_ORDER,
  HISTORICAL_STORE_METRIC_COLUMN_ORDER,
} from 'domains/aggregate-store-performance/utils/constants/AggregateStorePerformanceOverviewPage.constants'
import { type StoreMetric } from 'domains/aggregate-store-performance/types/AggregateStorePerformance.types'
import { filterData } from 'domains/aggregate-store-performance/components/export-button/utils/ExportUtils'

const StyledButton = styled(Button)({
  margin: 0,
  padding: `0 ${spacing.X12}`,
  height: '38px',
})

const Content = styled.div({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
})

const TextContainer = styled.div({
  marginLeft: spacing.X8,
})

interface ExportButtonProps {
  data: StoreMetric[]
  startDate: Moment
  isWeek: boolean
}

export const ExportButton: FunctionComponent<ExportButtonProps> = ({ data, startDate, isWeek }) => {
  const messages = useDomainMessages({
    export: 'aggregateStorePerformanceDomain.exportButton.export',
  })
  const retailerId = useRetailerId()
  const warehouseContext = useWarehouseContext()

  const onExportClicked = useCallback(async () => {
    const storeIds = data.map(row => row.storeId)
    const useDedicatedRates = hasDedicatedRates(data)
    trackExportClicked({ retailerId, storeIds, startDate, isWeek })

    let warehouseName = ''
    if (warehouseContext.loaded) {
      const { name } = warehouseContext.warehouse
      warehouseName = name
    }

    const filename = `${momentToQueryStringDate(isWeek ? toStartOfWeek(startDate) : startDate)}-${
      isWeek ? `to-${momentToQueryStringDate(getEndOfWeek(startDate))}-` : ''
    }${warehouseName ? `${warehouseName}-` : ''}store-performance.csv`

    const fieldsToOmit: (keyof StoreMetric)[] = ['storeId']
    if (useDedicatedRates) {
      fieldsToOmit.push('matchRate', 'utilization')
    } else {
      fieldsToOmit.push('dedicatedMatchRate', 'dedicatedUtilization')
    }

    const filteredData = filterData(data, fieldsToOmit)

    let columnOrder = HISTORICAL_STORE_METRIC_COLUMN_ORDER
    if (useDedicatedRates) {
      columnOrder = HISTORICAL_DEDICATED_STORE_METRIC_COLUMN_ORDER
    }

    const csvData = await jsonexport(filteredData, {
      headers: columnOrder,
    })

    downloadCsvFile(filename, csvData)
  }, [data, retailerId, startDate, isWeek, warehouseContext.loaded, warehouseContext.warehouse])

  return (
    <StyledButton variant="secondary" onClick={onExportClicked}>
      <Content>
        <DownloadIcon />
        <TextContainer>{messages.export}</TextContainer>
      </Content>
    </StyledButton>
  )
}
